import { useEffect, useState } from "react";
import { Loader2, Save, Lock, FolderSync, Unplug, RefreshCw, AlertCircle, CheckCircle2, Mic, Info, Link2, FileText, Sparkles, Download } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import ScorecardTemplatesPanel from "../components/Scorecards/ScorecardTemplatesPanel";
import BillingPanel from "../components/Billing/BillingPanel";
import CandidatePicker from "../components/CandidatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const STROKE = 1.5;

// Dev-only diagnostics (Pipeline timings). Unset in every shipped build, so
// the panel never renders in production; set REACT_APP_SHOW_DEV_PANELS=true
// locally to bring it back.
const SHOW_DEV_PANELS = process.env.REACT_APP_SHOW_DEV_PANELS === "true";

const RETENTION_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "indefinite", label: "Indefinite" },
];

const ASR_LANG_LABELS = {
  "unknown": "Auto-detect",
  "hi-IN": "Hindi",
  "en-IN": "English (India)",
  "ta-IN": "Tamil",
  "te-IN": "Telugu",
  "bn-IN": "Bengali",
  "mr-IN": "Marathi",
  "gu-IN": "Gujarati",
  "kn-IN": "Kannada",
  "ml-IN": "Malayalam",
  "pa-IN": "Punjabi",
  "od-IN": "Odia",
};

const DEFAULT_CONSENT = `Before we start, I'd like to record this interview so we have an accurate record of the conversation. The recording will be used only for internal hiring decisions and stored securely. Do you consent to being recorded?`;

export default function Settings() {
  const { workspace, member, refresh } = useAuth();
  const [name, setName] = useState("");
  const [retention, setRetention] = useState("365");
  const [consent, setConsent] = useState("");
  const [scorecard, setScorecard] = useState("");
  const [asrProvider, setAsrProvider] = useState("whisper");
  const [sarvamLang, setSarvamLang] = useState("unknown");
  const [asrConfig, setAsrConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = member?.role === "admin";

  useEffect(() => {
    if (workspace) {
      setName(workspace.name || "");
      setRetention(workspace.retention_policy || "365");
      setConsent(workspace.consent_text_override || "");
      setScorecard((workspace.default_scorecard_template || []).join("\n"));
      setAsrProvider(workspace.default_asr_provider || "whisper");
      setSarvamLang(workspace.sarvam_language_hint || "unknown");
    }
  }, [workspace]);

  useEffect(() => {
    api
      .get("/asr/config")
      .then(({ data }) => setAsrConfig(data))
      .catch(() => setAsrConfig(null));
  }, []);

  /**
   * Save-on-select for ASR fields ONLY.
   *
   * Why this exists (third regression of this dropdown):
   *   1. Original snap-back: dropdown reverted because there was no
   *      explicit Save button on the ASR row.
   *   2. May 30 incident: dropdown disabled-gate silently swallowed clicks
   *      on not-configured regions (Radix Select fires no onValueChange on
   *      disabled items), making selection look broken even though the
   *      backend would accept it.
   *   3. This patch: removes the disabled gate (the inline warning panel
   *      below handles not-configured state visually) AND adds an
   *      immediate PATCH so the dropdown selection persists without the
   *      user needing to click the page-level Save button. Backend
   *      accepts any of the three internal keys regardless of whether
   *      the provider is "configured" on this deployment — the routing
   *      layer falls back gracefully if a key is missing at transcribe
   *      time.
   *
   * Internal keys assemblyai/sarvam/whisper STAY untouched in this PATCH.
   * The save-on-select sends exactly the same field shape as the
   * page-level Save would.
   */
  const persistAsrField = async (patch) => {
    if (!isAdmin) return;
    try {
      await api.patch("/workspaces/me", patch);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const handleAsrProviderChange = (next) => {
    setAsrProvider(next);
    persistAsrField({ default_asr_provider: next });
  };

  const handleSarvamLangChange = (next) => {
    setSarvamLang(next);
    persistAsrField({ sarvam_language_hint: next });
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const scorecard_arr = scorecard
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.patch("/workspaces/me", {
        name,
        retention_policy: retention,
        consent_text_override: consent || null,
        default_scorecard_template: scorecard_arr,
        default_asr_provider: asrProvider,
        sarvam_language_hint: sarvamLang,
      });
      await refresh();
      toast.success("Workspace settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const readonly = !isAdmin;

  return (
    <AppShell>
      <div data-testid="settings-page" className="space-y-10">
        <header>
          <div className="hp-section-label">Settings</div>
          <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
            Workspace
          </h1>
          <p className="mt-1 text-[14px] text-ink-500">
            {readonly
              ? "Workspace settings are managed by your admin."
              : "Configure consent, retention, and scorecards for the whole team."}
          </p>
        </header>

        <section className="hp-card">
          <div className="border-b border-ink-200 px-6 py-4">
            <h2 className="font-display text-lg font-medium text-ink-900">General</h2>
          </div>
          <div className="space-y-6 p-6">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Workspace name
              </label>
              <input
                type="text"
                className="hp-input mt-1.5 max-w-md disabled:bg-ink-50"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readonly}
                data-testid="settings-workspace-name"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Retention policy
              </label>
              <p className="mt-1 text-[13px] text-ink-500">
                How long recordings, transcripts, and summaries are kept by default.
              </p>
              <div className="mt-2 max-w-md">
                <Select value={retention} onValueChange={setRetention} disabled={readonly}>
                  <SelectTrigger className="hp-input" data-testid="settings-retention-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RETENTION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`retention-option-${opt.value}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && <RetentionPreviewPanel />}
            </div>
          </div>
        </section>

        <section className="hp-card" data-testid="asr-panel">
          <div className="border-b border-ink-200 px-6 py-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-medium text-ink-900 flex items-center gap-2">
                <Mic className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
                Speech recognition
              </h2>
              <p className="mt-1 text-[13px] text-ink-500">
                Default speech recognition provider for new recordings in this workspace. Existing transcripts are not re-processed.
              </p>
            </div>
            {!isAdmin && <Lock className="h-4 w-4 text-ink-400" strokeWidth={STROKE} />}
          </div>
          <div className="space-y-6 p-6">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Speech recognition region
              </label>
              <p className="mt-1 text-[13px] text-ink-500">
                Pick the region tuned for your candidate pool. US &amp; Canada is best for English-language interviews. India &amp; Others handles Indian languages and Hindi-English code-switching.
              </p>
              <div className="mt-2 max-w-md">
                <Select
                  value={asrProvider}
                  onValueChange={handleAsrProviderChange}
                  disabled={readonly}
                >
                  <SelectTrigger className="hp-input" data-testid="settings-asr-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* NOTE — DO NOT add `disabled` props to these
                        SelectItems again. The May 30 regression came from
                        gating with `disabled={!configured}` — Radix Select
                        does not fire onValueChange on disabled items, so
                        the dropdown silently swallowed clicks on regions
                        that weren't configured on this deployment. The
                        inline warning panel below handles the
                        not-configured state visually; backend routing
                        falls back gracefully at transcribe time. Test
                        `test_phase4_ui_asr_dropdown_save_on_select`
                        is the release gate that asserts this contract. */}
                    {asrProvider === "whisper" && (
                      <SelectItem value="whisper" data-testid="asr-option-whisper">
                        Backup engine (legacy default)
                      </SelectItem>
                    )}
                    <SelectItem value="assemblyai" data-testid="asr-option-assemblyai">
                      US &amp; Canada — best for English-language interviews
                      {asrConfig && !asrConfig.assemblyai_configured && (
                        <span className="ml-2 text-[11px] text-amber-700">· Not configured</span>
                      )}
                    </SelectItem>
                    <SelectItem value="sarvam" data-testid="asr-option-sarvam">
                      India &amp; Others — Indian languages &amp; Hindi-English code-switching
                      {asrConfig && !asrConfig.sarvam_configured && (
                        <span className="ml-2 text-[11px] text-amber-700">· Not configured</span>
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {asrProvider === "assemblyai" && asrConfig && !asrConfig.assemblyai_configured && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="assemblyai-not-configured">
                  <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} />
                  <span>
                    US &amp; Canada region is selected but not configured on this deployment. New interviews will automatically fall back to a backup engine until it is set.
                  </span>
                </div>
              )}
              {asrProvider === "sarvam" && asrConfig && !asrConfig.sarvam_configured && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="sarvam-not-configured">
                  <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} />
                  <span>
                    India &amp; Others region is selected but not configured on this deployment. New interviews will automatically fall back to a backup engine until it is set.
                  </span>
                </div>
              )}
            </div>

            {asrProvider === "sarvam" && (
              <div data-testid="sarvam-lang-row">
                <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                  India &amp; Others — language hint
                </label>
                <p className="mt-1 text-[13px] text-ink-500">
                  Sent to the transcription engine on every call. Choose &quot;Auto-detect&quot; if your interviews mix languages unpredictably.
                </p>
                <div className="mt-2 max-w-md">
                  <Select value={sarvamLang} onValueChange={handleSarvamLangChange} disabled={readonly}>
                    <SelectTrigger className="hp-input" data-testid="settings-sarvam-lang-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(asrConfig?.sarvam_language_codes || Object.keys(ASR_LANG_LABELS)).map((code) => (
                        <SelectItem key={code} value={code} data-testid={`sarvam-lang-${code}`}>
                          {ASR_LANG_LABELS[code] || code} · <span className="font-mono text-[11px] text-ink-500">{code}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Phase 4a — Scorecard templates (admin-only UI, viewer/recruiter sees read-only) */}
        <section className="hp-card" data-testid="scorecard-templates-panel-section">
          <div className="border-b border-ink-200 px-6 py-4">
            <h2 className="font-display text-lg font-medium text-ink-900">Scorecard templates</h2>
            <p className="mt-1 text-[13px] text-ink-500">
              Default applies to every interview. Per-role templates override the default when present.
              3–8 competencies per template. Submitted scorecards keep their template snapshot — editing a template never alters past scorecards.
            </p>
          </div>
          <ScorecardTemplatesPanel readonly={readonly} />
        </section>

        <section className="hp-card">
          <div className="border-b border-ink-200 px-6 py-4">
            <h2 className="font-display text-lg font-medium text-ink-900">Consent script</h2>
            <p className="mt-1 text-[13px] text-ink-500">
              Shown to the recruiter before recording starts in Phase 2. The recruiter must read
              this aloud and check the consent box.
            </p>
          </div>
          <div className="p-6">
            <textarea
              rows={5}
              className="hp-input font-display text-[15px] leading-relaxed disabled:bg-ink-50"
              value={consent}
              placeholder={DEFAULT_CONSENT}
              onChange={(e) => setConsent(e.target.value)}
              disabled={readonly}
              data-testid="settings-consent"
            />
            <p className="mt-2 text-[12px] text-ink-400">
              Leave blank to use the default. Default is shown in the placeholder.
            </p>
          </div>
        </section>

        <section className="hp-card">
          <div className="border-b border-ink-200 px-6 py-4">
            <h2 className="font-display text-lg font-medium text-ink-900">
              Default scorecard template
            </h2>
            <p className="mt-1 text-[13px] text-ink-500">
              One competency per line. Each interview pre-fills with these by default.
            </p>
          </div>
          <div className="p-6">
            <textarea
              rows={6}
              className="hp-input font-mono text-[13px] disabled:bg-ink-50"
              value={scorecard}
              onChange={(e) => setScorecard(e.target.value)}
              disabled={readonly}
              data-testid="settings-scorecard"
            />
          </div>
        </section>

        {!readonly && (
          <div className="flex items-center justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="hp-btn-primary"
              data-testid="settings-save"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={STROKE} />
              )}
              Save changes
            </button>
          </div>
        )}

        <section className="hp-card">
          <BillingPanel isAdmin={isAdmin} />
        </section>

        {isAdmin && <AuditExportPanel />}
        <DrivePanel isAdmin={isAdmin} />
        {isAdmin && <UnlinkedInterviewsPanel />}
        {isAdmin && SHOW_DEV_PANELS && <PipelineTimingsPanel />}
      </div>
    </AppShell>
  );
}

function RetentionPreviewPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/workspaces/me/retention-preview")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <div className="mt-3 rounded-md border border-ink-200 bg-ink-50 p-3 text-[13px] text-ink-700" data-testid="retention-preview">
      <p>{data.plain_language}</p>
      <p className="mt-1.5 text-[12px] text-ink-500">
        {data.sweep_enabled
          ? data.sweep_dry_run
            ? "Automatic sweep is ON in dry-run mode — nothing is deleted yet."
            : "Automatic sweep is LIVE."
          : "Automatic sweep is currently OFF."}
        {" "}Runs every {data.sweep_interval_hours}h.
        {data.next_scheduled_sweep_at && (
          <> Next run: {new Date(data.next_scheduled_sweep_at).toLocaleString()}.</>
        )}
        {" "}<span className="font-medium text-ink-900" data-testid="retention-candidates-in-window">{data.candidates_in_window}</span>{" "}
        candidate{data.candidates_in_window === 1 ? "" : "s"} currently fall within the deletion window.
      </p>
    </div>
  );
}


function AuditExportPanel() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/audit-log/export", { params, responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "audit_log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="hp-card" data-testid="audit-export-panel">
      <div className="border-b border-ink-200 px-6 py-4">
        <h2 className="font-display text-lg font-medium text-ink-900">Audit log export</h2>
        <p className="mt-1 text-[13px] text-ink-500">
          CSV of who did what, when. Timestamp, actor, action, resource — never transcript content, CV text, or candidate PII beyond an id.
        </p>
      </div>
      <div className="p-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">From</label>
          <input type="date" className="hp-input mt-1.5" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="audit-export-start-date" />
        </div>
        <div>
          <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">To</label>
          <input type="date" className="hp-input mt-1.5" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="audit-export-end-date" />
        </div>
        <button onClick={handleExport} disabled={exporting} className="hp-btn-primary" data-testid="audit-export-download">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Download className="h-4 w-4" strokeWidth={STROKE} />}
          Download CSV
        </button>
      </div>
    </section>
  );
}

function DrivePanel({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: s } = await api.get("/drive/status");
      setStatus(s);
      if (isAdmin && s.connected) {
        const { data: l } = await api.get("/drive/sync-log");
        setLogs(l);
      }
    } catch {
      // Drive endpoint may not be available — fail gracefully.
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // Handle ?drive=connected redirect from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connected");
      // strip query
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Drive connect failed");
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Google Drive? Future interviews won't be synced until you reconnect.")) return;
    setBusy(true);
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive disconnected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="hp-card" data-testid="drive-panel">
      <div className="border-b border-ink-200 px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-medium text-ink-900 flex items-center gap-2">
            <FolderSync className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
            Google Drive sync
          </h2>
          <p className="mt-1 text-[13px] text-ink-500">
            After each interview is transcribed, the audio + transcript + summary are pushed to your Drive in a structured folder.
          </p>
        </div>
        {!isAdmin && <Lock className="h-4 w-4 text-ink-400" strokeWidth={STROKE} />}
      </div>
      <div className="p-6 space-y-5">
        {loading && <Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} />}
        {!loading && status && !status.configured && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="drive-not-configured">
            <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} />
            <span>
              Google Drive integration isn&rsquo;t configured for this deployment yet. Your platform admin needs to set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET / GOOGLE_DRIVE_REDIRECT_URI in the backend environment, then redeploy.
            </span>
          </div>
        )}
        {!loading && status?.configured && !status.connected && (
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="drive-not-connected">
            <div className="text-[14px] text-ink-700">Not connected. Folder layout: <code className="font-mono text-[12px] text-ink-500">Hiretape / [Role] / [Candidate] / Round [N]</code></div>
            <button onClick={handleConnect} disabled={!isAdmin || busy} className="hp-btn-primary" data-testid="drive-connect-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
              Connect Google Drive
            </button>
          </div>
        )}
        {!loading && status?.connected && (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="drive-connected">
              <div className="flex items-center gap-2 text-[14px]">
                <CheckCircle2 className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
                <span className="font-medium text-ink-900">{status.connected_email || "Connected"}</span>
                <span className="text-ink-500">· since {new Date(status.connected_at).toLocaleDateString()}</span>
              </div>
              {isAdmin && (
                <button onClick={handleDisconnect} disabled={busy} className="hp-btn-outline" data-testid="drive-disconnect-btn">
                  <Unplug className="h-4 w-4" strokeWidth={STROKE} /> Disconnect
                </button>
              )}
            </div>
            {logs.length > 0 && (
              <div>
                <div className="hp-section-label mb-2">Recent syncs</div>
                <div className="rounded-md border border-ink-200 divide-y divide-ink-100" data-testid="drive-sync-log">
                  {logs.slice(0, 8).map((l) => (
                    <div key={l.log_id} className="flex items-center justify-between px-3 py-2 text-[13px]">
                      <div className="flex items-center gap-2">
                        {l.status === "completed" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} />}
                        {l.status === "failed" && <AlertCircle className="h-3.5 w-3.5 text-red-700" strokeWidth={STROKE} />}
                        {l.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-400" strokeWidth={STROKE} />}
                        {l.status === "pending" && <RefreshCw className="h-3.5 w-3.5 text-ink-400" strokeWidth={STROKE} />}
                        <span className="text-ink-700">Interview {l.interview_id}</span>
                      </div>
                      <span className="text-[12px] text-ink-500">{l.status} · {new Date(l.created_at).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function PipelineTimingsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/transcribe/timings/recent?limit=25");
      setRows(data.jobs || []);
    } catch (err) {
      console.warn("pipeline timings load failed:", err?.message || err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmtMs = (ms) => {
    if (ms === null || ms === undefined) return "—";
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  return (
    <section className="hp-card" data-testid="pipeline-timings-panel">
      <div className="border-b border-ink-200 px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-medium text-ink-900">
            Pipeline timings <span className="ml-2 text-[11px] font-medium uppercase tracking-wider text-emerald-800/70">dev</span>
          </h2>
          <p className="mt-1 text-[13px] text-ink-500">
            Per-stage durations for recent transcription jobs. Use this to identify which stage is the bottleneck before deciding what to optimize next.
          </p>
        </div>
        <button
          className="hp-btn-secondary"
          onClick={load}
          disabled={refreshing}
          data-testid="pipeline-timings-refresh"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <div className="p-6 overflow-x-auto">
        {loading ? (
          <div className="text-[13px] text-ink-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[13px] text-ink-500">No transcription jobs yet. Run an interview to populate this view.</div>
        ) : (
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-ink-200 text-left text-ink-500">
                <th className="py-2 pr-3 font-medium">Interview</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">ASR</th>
                <th className="py-2 pr-3 font-medium">Summary</th>
                <th className="py-2 pr-3 font-medium">Roles</th>
                <th className="py-2 pr-3 font-medium">Post-phase</th>
                <th className="py-2 pr-3 font-medium font-mono text-emerald-900">Wall clock</th>
                <th className="py-2 pr-3 font-medium">Chunks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const t = r.stage_timings || {};
                const chunks = t.per_chunk_asr_durations || [];
                const summaryRolesParallel = (t.post_phase_total_ms && t.summary_ms && t.role_classification_ms)
                  ? t.post_phase_total_ms < (t.summary_ms + t.role_classification_ms) * 0.85
                  : false;
                return (
                  <tr key={r.job_id} className="border-b border-ink-100 align-top">
                    <td className="py-2 pr-3 font-mono text-[11px]">{r.interview_id || r.job_id}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block rounded border px-1.5 py-px text-[10px] font-medium ${r.status === "completed" ? "border-emerald-700/30 bg-emerald-50 text-emerald-900" : r.status === "failed" ? "border-red-300 bg-red-50 text-red-900" : "border-ink-200 bg-ink-50 text-ink-700"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono">{fmtMs(t.asr_total_ms)}</td>
                    <td className="py-2 pr-3 font-mono">{fmtMs(t.summary_ms)}</td>
                    <td className="py-2 pr-3 font-mono">{fmtMs(t.role_classification_ms)}</td>
                    <td className="py-2 pr-3 font-mono">
                      {fmtMs(t.post_phase_total_ms)}
                      {summaryRolesParallel && <span className="ml-1 text-[10px] text-emerald-800" title="Summary + roles ran in parallel">∥</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-emerald-900 font-semibold">{fmtMs(t.total_wall_clock_ms)}</td>
                    <td className="py-2 pr-3 font-mono text-ink-500">
                      {chunks.length > 0 ? (
                        <span title={chunks.map((c, i) => `#${i}: ${c.provider} ${c.outcome} ${c.duration_ms}ms`).join("\n")}>
                          {chunks.length} ({chunks.map((c) => c.provider).join("→")})
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="mt-4 rounded-md border border-ink-200 bg-ink-50 p-3 text-[11px] text-ink-600">
          <strong>Reading the table:</strong> ASR = transcription provider call. Summary &amp; Roles are independent Gemini calls running in parallel — &quot;Post-phase&quot; should be close to <span className="font-mono">max(Summary, Roles)</span> if the parallelism is working (look for the <span className="text-emerald-800 font-mono">∥</span> marker). Wall clock includes everything from <span className="font-mono">processing_start_at</span> to job completion.
        </div>
      </div>
    </section>
  );
}



function UnlinkedInterviewsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/interviews/unlinked/admin`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load unlinked interviews");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function handleLink(interviewId, candidate_id) {
    try {
      await api.post(`/interviews/${interviewId}/assign-candidate`, { candidate_id });
      toast.success("Linked");
      setLinkingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link");
    }
  }

  return (
    <section className="space-y-6" data-testid="unlinked-interviews-panel">
      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-medium text-ink-900">Unlinked interviews</h2>
          {data && (
            <span className="rounded-md bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-700">
              {data.total} total
            </span>
          )}
        </div>
        <p className="mt-1 text-[12px] text-ink-500">
          Interviews not yet linked to a candidate. These don't appear on any candidate profile and aren't searchable from the AI chat. Link them here.
        </p>
      </div>

      {loading && <Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} />}

      {data && data.total === 0 && (
        <div className="hp-card border-emerald-200 bg-emerald-50/40 p-4 text-[13px] text-emerald-900 flex items-center gap-2" data-testid="unlinked-empty">
          <CheckCircle2 className="h-4 w-4" strokeWidth={STROKE} />
          All interviews are linked to candidates. Nothing to rescue.
        </div>
      )}

      {data && data.groups?.map((g) => (
        <div key={g.created_by} className="hp-card p-4 space-y-3">
          <div className="text-[12px] font-medium text-ink-700">
            Created by <span className="text-ink-900">{g.created_by_name}</span>
            <span className="text-ink-500"> · {g.created_by_email}</span>
          </div>
          <div className="space-y-2">
            {g.interviews.map((iv) => (
              <div key={iv.interview_id} className="flex items-center justify-between gap-3 rounded-md border border-ink-200 bg-white p-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-ink-900">{iv.title}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-500">
                    <span>{new Date(iv.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span className="capitalize">{iv.type || "—"}</span>
                    {iv.has_transcript && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-800"><FileText className="inline h-2.5 w-2.5 mr-0.5" strokeWidth={STROKE} />transcript</span>}
                    {iv.has_summary && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-800"><Sparkles className="inline h-2.5 w-2.5 mr-0.5" strokeWidth={STROKE} />summary</span>}
                  </div>
                </div>
                <button
                  onClick={() => setLinkingId(iv.interview_id)}
                  className="rounded-md border border-emerald-800 bg-white px-2.5 py-1 text-[12px] font-medium text-emerald-900 hover:bg-emerald-50"
                  data-testid={`unlinked-link-${iv.interview_id}`}
                >
                  <Link2 className="inline h-3 w-3 mr-1" strokeWidth={STROKE} />
                  Link
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={Boolean(linkingId)} onOpenChange={(v) => !v && setLinkingId(null)}>
        <DialogContent data-testid="unlinked-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium">Link to candidate</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-500">
              Pick the candidate this interview is for. Round number auto-computes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {linkingId && (
              <CandidatePicker
                onSelect={({ candidate_id }) => handleLink(linkingId, candidate_id)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
